import React, {useCallback, useEffect, useRef, useState} from "react";
import {
  Controller,
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
} from "react-hook-form";
import {Input, Switch, Select} from "antd";
import {EyeOutlined, InfoCircleOutlined} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import type ReactQuillType from "react-quill";
import type Quill from "quill";
import {
  StyledFormContainer,
  StyledFormItem,
  StyledQuillWrapper,
  StyledPreviewButton,
  StyledTimingCard,
} from "../../automations.style";
import type {CreateAutomationFormValues} from "./schema";
import {helperTextForTriggerQuotaBlocked} from "./email-automation-limits";
import {
  parseIncompleteShortCodePrefix,
  replaceShortCodeToken,
  shortCodeCompletionCandidates,
} from "./short-code-completion-helpers";
import {
  ShortCodeCompletionDropdown,
  type ShortCodeCompletionItem,
} from "./ShortCodeCompletionDropdown";

interface CreateAutomationFormProps {
  control: Control<CreateAutomationFormValues>;
  errors: FieldErrors<CreateAutomationFormValues>;
  watch: (name: keyof CreateAutomationFormValues) => unknown;
  setValue: UseFormSetValue<CreateAutomationFormValues>;
  clearErrors: UseFormClearErrors<CreateAutomationFormValues>;
  offeringOptions: {value: string; label: string}[];
  isOfferingsLoading?: boolean;
  subjectInputRef: React.MutableRefObject<HTMLInputElement | null>;
  quillRef: React.MutableRefObject<ReactQuillType | null>;
  subjectCursorPos: React.MutableRefObject<number | null>;
  bodyCursorPos: React.MutableRefObject<number | null>;
  onOpenShortCodes: (target: "subject" | "body") => void;
  onOpenPreview: () => void;
  /** Offering wizard: context offering is implicit; omit multi-select UX. */
  hideOfferingsAssignmentField?: boolean;
  /** Blocks trigger options still at account max (typically create flow only). */
  triggerQuotaBlocked?: Partial<Record<"after_purchase" | "abandoned_purchase", boolean>>;
}

/** `@types/quill` omits `container`; Quill 1.x assigns it on the instance at runtime. */
type QuillWithContainer = Quill & {container: HTMLElement};

function getQuillFromRef(
  qr: React.MutableRefObject<ReactQuillType | null>
): QuillWithContainer | undefined {
  const editor = qr.current?.getEditor();
  return editor ? (editor as QuillWithContainer) : undefined;
}

type ShortCodeCompletionState = {
  field: "subject" | "body";
  items: ShortCodeCompletionItem[];
  selectedIndex: number;
  anchor: {top: number; left: number; minWidth: number};
  braceStart: number;
  replaceEnd: number;
  /** Latest subject value when completing subject (ignored for body). */
  subjectSnapshot: string;
};

export function CreateAutomationForm({
  control,
  errors,
  watch,
  setValue,
  clearErrors,
  offeringOptions,
  isOfferingsLoading = false,
  subjectInputRef,
  quillRef,
  subjectCursorPos,
  bodyCursorPos,
  onOpenShortCodes,
  onOpenPreview,
  hideOfferingsAssignmentField = false,
  triggerQuotaBlocked,
}: CreateAutomationFormProps) {
  const addCustomButton = watch("addCustomButton") as boolean;
  const currentTrigger = watch("trigger") as string;
  const currentSendTiming = watch("sendTiming") as string;
  const triggerQuotaHelperText = helperTextForTriggerQuotaBlocked(triggerQuotaBlocked);

  const [shortCodeCompletion, setShortCodeCompletion] =
    useState<ShortCodeCompletionState | null>(null);
  const shortCodeCompletionRef = useRef<ShortCodeCompletionState | null>(null);
  shortCodeCompletionRef.current = shortCodeCompletion;

  const handleSendTimingChange = useCallback(
    (sendTiming: "immediately" | "custom_delay") => {
      setValue("sendTiming", sendTiming, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      if (sendTiming === "immediately") {
        clearErrors(["delayValue", "delayUnit"]);
      }
    },
    [clearErrors, setValue]
  );

  const isAbandonedPurchase = currentTrigger === "abandoned_purchase";

  useEffect(() => {
    if (isAbandonedPurchase) {
      setValue("delayValue", 30);
      setValue("delayUnit", "minutes");
      setValue("sendTiming", "custom_delay");
      clearErrors(["delayValue", "delayUnit"]);
    }
  }, [isAbandonedPurchase, setValue, clearErrors]);

  const applyShortCodeCompletion = useCallback(
    (code: string) => {
      const c = shortCodeCompletionRef.current;
      if (!c) return;

      if (c.field === "subject") {
        const {value, caret} = replaceShortCodeToken(
          c.subjectSnapshot,
          c.braceStart,
          c.replaceEnd,
          code
        );
        setValue("subject", value, {shouldValidate: true});
        setShortCodeCompletion(null);
        requestAnimationFrame(() => {
          const input = subjectInputRef.current;
          if (input) input.setSelectionRange(caret, caret);
        });
        return;
      }

      const editor = getQuillFromRef(quillRef);
      const range = editor?.getSelection();
      if (!editor || range == null) {
        setShortCodeCompletion(null);
        return;
      }
      const end = range.index;
      editor.deleteText(c.braceStart, end - c.braceStart);
      editor.insertText(c.braceStart, code);
      editor.setSelection(c.braceStart + code.length, 0);
      setValue("body", editor.root.innerHTML, {shouldValidate: true});
      setShortCodeCompletion(null);
    },
    [quillRef, setValue, subjectInputRef]
  );

  const syncSubjectShortCodeCompletion = useCallback(
    (value: string, cursorPos: number) => {
      const before = value.slice(0, cursorPos);
      const token = parseIncompleteShortCodePrefix(before);
      if (!token) {
        setShortCodeCompletion(null);
        return;
      }
      const items = shortCodeCompletionCandidates(token.inner);
      if (items.length === 0) {
        setShortCodeCompletion(null);
        return;
      }
      const input = subjectInputRef.current;
      const rect = input?.getBoundingClientRect();
      if (!rect) return;
      setShortCodeCompletion({
        field: "subject",
        items,
        selectedIndex: 0,
        anchor: {top: rect.bottom + 6, left: rect.left, minWidth: rect.width},
        braceStart: token.braceStart,
        replaceEnd: cursorPos,
        subjectSnapshot: value,
      });
    },
    [subjectInputRef]
  );

  const syncBodyShortCodeCompletion = useCallback(() => {
    const editor = getQuillFromRef(quillRef);
    if (!editor) {
      setShortCodeCompletion(null);
      return;
    }
    const range = editor.getSelection();
    if (!range) {
      setShortCodeCompletion(null);
      return;
    }
    const before = editor.getText(0, range.index);
    const token = parseIncompleteShortCodePrefix(before);
    if (!token) {
      setShortCodeCompletion(null);
      return;
    }
    const items = shortCodeCompletionCandidates(token.inner);
    if (items.length === 0) {
      setShortCodeCompletion(null);
      return;
    }
    const bounds = editor.getBounds(range.index);
    const container = editor.container.getBoundingClientRect();
    setShortCodeCompletion({
      field: "body",
      items,
      selectedIndex: 0,
      anchor: {
        top: container.top + bounds.top + bounds.height + 6,
        left: container.left + bounds.left,
        minWidth: Math.max(200, container.width * 0.5),
      },
      braceStart: token.braceStart,
      replaceEnd: range.index,
      subjectSnapshot: "",
    });
  }, [quillRef]);

  /** Tab opens menu when closed if prefix matches; arrows / Tab / Enter when open. */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const open = shortCodeCompletionRef.current;

      if (e.key === "Escape" && open) {
        e.preventDefault();
        setShortCodeCompletion(null);
        return;
      }

      if (open) {
        if (e.key === "Tab" && e.shiftKey) {
          e.preventDefault();
          setShortCodeCompletion(null);
          return;
        }
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setShortCodeCompletion((prev) =>
            prev
              ? {
                  ...prev,
                  selectedIndex: Math.min(prev.selectedIndex + 1, prev.items.length - 1),
                }
              : prev
          );
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setShortCodeCompletion((prev) =>
            prev
              ? {
                  ...prev,
                  selectedIndex: Math.max(prev.selectedIndex - 1, 0),
                }
              : prev
          );
          return;
        }
        if (e.key === "Tab" && !e.shiftKey) {
          e.preventDefault();
          const item = open.items[open.selectedIndex];
          if (item) applyShortCodeCompletion(item.code);
          return;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          const item = open.items[open.selectedIndex];
          if (item) applyShortCodeCompletion(item.code);
        }
        return;
      }

      if (e.key !== "Tab" || e.shiftKey) return;

      const target = e.target as Node | null;
      const subjectEl = subjectInputRef.current;
      const quillEditor = getQuillFromRef(quillRef);
      const quillContainer = quillEditor?.container;

      if (subjectEl && (target === subjectEl || subjectEl.contains(target as Node))) {
        const v = (subjectEl as HTMLInputElement).value;
        const pos = (subjectEl as HTMLInputElement).selectionStart ?? v.length;
        const token = parseIncompleteShortCodePrefix(v.slice(0, pos));
        if (!token) return;
        const items = shortCodeCompletionCandidates(token.inner);
        if (items.length === 0) return;
        e.preventDefault();
        syncSubjectShortCodeCompletion(v, pos);
        return;
      }

      if (quillContainer && quillEditor && target && quillContainer.contains(target)) {
        const range = quillEditor.getSelection();
        if (!range) return;
        const before = quillEditor.getText(0, range.index);
        const token = parseIncompleteShortCodePrefix(before);
        if (!token) return;
        const items = shortCodeCompletionCandidates(token.inner);
        if (items.length === 0) return;
        e.preventDefault();
        syncBodyShortCodeCompletion();
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [
    applyShortCodeCompletion,
    quillRef,
    subjectInputRef,
    syncBodyShortCodeCompletion,
    syncSubjectShortCodeCompletion,
  ]);

  useEffect(() => {
    if (!shortCodeCompletion) return;
    const close = (ev: MouseEvent) => {
      const t = ev.target as Node;
      const listbox = document.querySelector("[data-short-code-completion-menu]");
      if (listbox?.contains(t)) return;
      if (subjectInputRef.current?.contains(t)) return;
      const editor = getQuillFromRef(quillRef);
      if (editor?.container.contains(t)) return;
      setShortCodeCompletion(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [shortCodeCompletion, quillRef, subjectInputRef]);

  return (
    <>
      <StyledFormContainer>
        <div className="form-header">Email information</div>

        <div style={{backgroundColor: "white", padding: "12px", borderRadius: "12px"}}>
          <StyledFormItem>
            <div className="label-row">
              <label>Automation name</label>
            </div>
            <Controller
              name="automationName"
              control={control}
              render={({field}) => (
                <Input
                  {...field}
                  placeholder="e.g Course purchase email"
                  status={errors.automationName ? "error" : undefined}
                />
              )}
            />
            {errors.automationName && (
              <div role="alert" className="ant-form-item-explain-error">
                {errors.automationName.message}
              </div>
            )}
          </StyledFormItem>

          <StyledFormItem>
            <div className="label-row">
              <label>Subject</label>
              <span
                className="insert-link"
                onClick={() => {
                  if (subjectInputRef.current) {
                    subjectCursorPos.current =
                      subjectInputRef.current.selectionStart ?? null;
                  }
                  onOpenShortCodes("subject");
                }}
              >
                Insert short codes
              </span>
            </div>
            <Controller
              name="subject"
              control={control}
              render={({field}) => (
                <Input
                  {...field}
                  ref={(el) => {
                    subjectInputRef.current = el?.input ?? null;
                  }}
                  status={errors.subject ? "error" : undefined}
                  onChange={(e) => {
                    field.onChange(e);
                    requestAnimationFrame(() => {
                      const el = e.target;
                      syncSubjectShortCodeCompletion(
                        el.value,
                        el.selectionStart ?? el.value.length
                      );
                    });
                  }}
                  onSelect={(e) => {
                    const el = e.currentTarget;
                    syncSubjectShortCodeCompletion(
                      typeof field.value === "string" ? field.value : "",
                      el.selectionStart ??
                        (typeof field.value === "string" ? field.value.length : 0)
                    );
                  }}
                  onClick={(e) => {
                    const el = e.currentTarget;
                    syncSubjectShortCodeCompletion(
                      typeof field.value === "string" ? field.value : "",
                      el.selectionStart ??
                        (typeof field.value === "string" ? field.value.length : 0)
                    );
                  }}
                  onKeyUp={(e) => {
                    const el = e.currentTarget;
                    syncSubjectShortCodeCompletion(
                      typeof field.value === "string" ? field.value : "",
                      el.selectionStart ??
                        (typeof field.value === "string" ? field.value.length : 0)
                    );
                  }}
                />
              )}
            />
            {errors.subject && (
              <div role="alert" className="ant-form-item-explain-error">
                {errors.subject.message}
              </div>
            )}
          </StyledFormItem>

          <StyledFormItem style={{marginBottom: 0}}>
            <div className="label-row">
              <label>Body</label>
              <span className="insert-link" onClick={() => onOpenShortCodes("body")}>
                Insert short codes
              </span>
            </div>
            <Controller
              name="body"
              control={control}
              render={({field: {onChange, value}}) => (
                <StyledQuillWrapper>
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={value}
                    onChange={(content, _delta, _source, editor) => {
                      onChange(content);
                      const sel = editor.getSelection();
                      if (sel) bodyCursorPos.current = sel.index;
                      requestAnimationFrame(() => syncBodyShortCodeCompletion());
                    }}
                    onChangeSelection={(selection) => {
                      if (selection) {
                        bodyCursorPos.current = selection.index;
                      }
                      requestAnimationFrame(() => syncBodyShortCodeCompletion());
                    }}
                    modules={{
                      toolbar: [
                        ["bold", "italic", "underline", "strike"],
                        [{align: []}],
                        [{list: "ordered"}, {list: "bullet"}],
                        ["link"],
                      ],
                    }}
                  />
                </StyledQuillWrapper>
              )}
            />
            {errors.body && (
              <div role="alert" className="ant-form-item-explain-error">
                {errors.body.message}
              </div>
            )}
          </StyledFormItem>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "16px",
              marginBottom: addCustomButton ? "16px" : "24px",
            }}
          >
            <span style={{fontSize: "14px", fontWeight: 600, color: "#1c1726"}}>
              Add custom button
            </span>
            <Controller
              name="addCustomButton"
              control={control}
              render={({field: {onChange, value}}) => (
                <Switch checked={value} onChange={onChange} />
              )}
            />
          </div>

          {addCustomButton && (
            <div style={{marginBottom: "24px"}}>
              <StyledFormItem style={{marginBottom: "16px"}}>
                <div className="label-row">
                  <label>Button text</label>
                </div>
                <Controller
                  name="buttonText"
                  control={control}
                  render={({field}) => (
                    <Input
                      {...field}
                      placeholder="Schedule a clarity call"
                      status={errors.buttonText ? "error" : undefined}
                    />
                  )}
                />
                {errors.buttonText && (
                  <div role="alert" className="ant-form-item-explain-error">
                    {errors.buttonText.message}
                  </div>
                )}
              </StyledFormItem>
              <StyledFormItem style={{marginBottom: 0}}>
                <div className="label-row">
                  <label>Button link</label>
                </div>
                <Controller
                  name="buttonLink"
                  control={control}
                  render={({field}) => (
                    <Input
                      {...field}
                      placeholder="Paste a valid link"
                      status={errors.buttonLink ? "error" : undefined}
                    />
                  )}
                />
                {errors.buttonLink && (
                  <div role="alert" className="ant-form-item-explain-error">
                    {errors.buttonLink.message}
                  </div>
                )}
              </StyledFormItem>
            </div>
          )}

          <StyledPreviewButton
            icon={<EyeOutlined />}
            onClick={onOpenPreview}
            style={{marginBottom: "24px", borderRadius: "12px"}}
          >
            Preview
          </StyledPreviewButton>
        </div>
      </StyledFormContainer>

      <StyledFormContainer>
        <div className="form-header">Settings</div>

        <div style={{backgroundColor: "white", padding: "12px", borderRadius: "12px"}}>
          <StyledFormItem style={{marginBottom: "12px"}}>
            <div className="label-row">
              <label>Trigger</label>
            </div>
            <Controller
              name="trigger"
              control={control}
              render={({field}) => (
                <Select
                  {...field}
                  style={{width: "100%"}}
                  options={[
                    {
                      value: "after_purchase",
                      label: "After purchase",
                      disabled: Boolean(triggerQuotaBlocked?.after_purchase),
                    },
                    {
                      value: "abandoned_purchase",
                      label: "Abandoned purchase",
                      disabled: Boolean(triggerQuotaBlocked?.abandoned_purchase),
                    },
                  ]}
                />
              )}
            />
            {triggerQuotaHelperText ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "8px",
                  color: "#595959",
                  fontSize: "12px",
                  marginTop: "8px",
                }}
              >
                <InfoCircleOutlined style={{marginTop: "4px", color: "#222222"}} />
                <span>{triggerQuotaHelperText}</span>
              </div>
            ) : null}
          </StyledFormItem>

          {!hideOfferingsAssignmentField ? (
            <StyledFormItem style={{marginBottom: "12px"}}>
              <div className="label-row">
                <label>
                  {currentTrigger === "after_purchase"
                    ? "Select the offering(s) that will be assigned to this email"
                    : "Select the offering(s) that will trigger this email"}
                </label>
              </div>
              <Controller
                name="offerings"
                control={control}
                render={({field}) => (
                  <Select
                    {...field}
                    mode="multiple"
                    showSearch
                    filterOption={(input, option) =>
                      ((option?.label as string) ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    style={{width: "100%"}}
                    placeholder="Search and select offerings"
                    options={offeringOptions}
                    loading={isOfferingsLoading}
                    disabled={isOfferingsLoading}
                    notFoundContent={
                      isOfferingsLoading ? "Loading offerings..." : "No offerings found"
                    }
                  />
                )}
              />
              {errors.offerings && (
                <div role="alert" className="ant-form-item-explain-error">
                  {errors.offerings.message}
                </div>
              )}
            </StyledFormItem>
          ) : null}

          <div style={{marginBottom: "16px"}}>
            <label
              style={{
                fontSize: "14px",
                color: "#1c1726",
                display: "block",
                marginBottom: "8px",
              }}
            >
              When should this email be sent?
            </label>

            {currentTrigger === "after_purchase" && (
              <div style={{display: "flex", gap: "16px", marginBottom: "16px"}}>
                <StyledTimingCard
                  active={currentSendTiming === "immediately"}
                  onClick={() => handleSendTimingChange("immediately")}
                >
                  <div className="timing-header">
                    <div className="timing-radio" />
                    <div className="timing-title">Immediately</div>
                  </div>
                  <div className="timing-desc">Send right after purchase</div>
                </StyledTimingCard>

                <StyledTimingCard
                  active={currentSendTiming === "custom_delay"}
                  onClick={() => handleSendTimingChange("custom_delay")}
                >
                  <div className="timing-header">
                    <div className="timing-radio" />
                    <div className="timing-title">Custom delay</div>
                  </div>
                  <div className="timing-desc">Define a specific wait time</div>
                </StyledTimingCard>
              </div>
            )}

            {(currentTrigger === "abandoned_purchase" ||
              currentSendTiming === "custom_delay") && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "16px",
                }}
              >
                <span style={{fontSize: "14px", color: "#1c1726"}}>Send after</span>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "flex-start",
                  }}
                >
                  <div>
                    <Controller
                      name="delayValue"
                      control={control}
                      render={({field}) => (
                        <Input
                          {...field}
                          type="number"
                          min={isAbandonedPurchase ? 30 : 5}
                          disabled={isAbandonedPurchase}
                          style={{
                            width: "70px",
                          }}
                          status={errors.delayValue ? "error" : undefined}
                        />
                      )}
                    />
                  </div>
                  <div>
                    <Controller
                      name="delayUnit"
                      control={control}
                      render={({field}) => (
                        <Select
                          {...field}
                          disabled={isAbandonedPurchase}
                          style={{width: "120px"}}
                          options={[
                            {value: "minutes", label: "Minutes"},
                            {value: "hours", label: "Hours"},
                            {value: "days", label: "Days"},
                          ]}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            )}

            {(errors.delayValue || errors.delayUnit) && (
              <div
                role="alert"
                className="ant-form-item-explain-error"
                style={{marginBottom: "16px"}}
              >
                {errors.delayValue?.message || errors.delayUnit?.message}
              </div>
            )}

            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                color: "#595959",
                fontSize: "12px",
              }}
            >
              <InfoCircleOutlined style={{marginTop: "4px", color: "#222222"}} />
              <span>
                {currentTrigger === "after_purchase"
                  ? "The automation trigger is activated as soon as a customer successfully completes a purchase"
                  : "The automation trigger is activated 30 minutes after a customer abandons a purchase"}
              </span>
            </div>
          </div>
        </div>
      </StyledFormContainer>

      <ShortCodeCompletionDropdown
        visible={shortCodeCompletion != null}
        items={shortCodeCompletion?.items ?? []}
        selectedIndex={shortCodeCompletion?.selectedIndex ?? 0}
        anchor={shortCodeCompletion?.anchor ?? {top: 0, left: 0, minWidth: 200}}
        onSelect={applyShortCodeCompletion}
        onHighlightIndex={(i) =>
          setShortCodeCompletion((prev) =>
            prev && prev.items.length ? {...prev, selectedIndex: i} : prev
          )
        }
      />
    </>
  );
}
